/**
 * EmailQueueHealthBanner — TODO-MDC-04.
 *
 * Lee `email_queue_health_summary(empresa_id)` y muestra banner amber
 * cuando la cola está `stuck` (cron caído) o `atrasado` (lento). Permite
 * al usuario detectar inmediatamente que sus mails no están saliendo
 * sin tener que ir a buscar el tab Envíos.
 *
 * El estado `inactivo` (no hay actividad en 24h) NO dispara banner —
 * eso es normal cuando no hay nada que enviar.
 *
 * Recargas: cada 60s para evitar polling agresivo. El banner es informativo,
 * no necesita reactividad sub-minuto.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Activity } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface Props {
  empresaId: string;
}

interface HealthSummary {
  empresa_id: string;
  empresa_nombre: string;
  overdue: number;
  pending: number;
  sending: number;
  recent_sent_1h: number;
  recent_sent_24h: number;
  failed_total: number;
  failed_24h: number;
  too_large_total: number;
  ultimo_envio_at: string | null;
  estado_health: 'ok' | 'atrasado' | 'stuck' | 'inactivo';
}

const POLL_INTERVAL_MS = 60_000;

export function EmailQueueHealthBanner({ empresaId }: Props) {
  const [summary, setSummary] = useState<HealthSummary | null>(null);

  const reload = useCallback(async () => {
    const { data, error } = await supabase.rpc('email_queue_health_summary', {
      p_empresa_id: empresaId,
    });
    if (error || !data) return;
    // RPC retorna TABLE, viene como array
    const first = Array.isArray(data) ? (data[0] as HealthSummary | undefined) : null;
    if (first) setSummary(first);
  }, [empresaId]);

  useEffect(() => {
    void reload();
    const id = setInterval(() => {
      void reload();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [reload]);

  if (!summary) return null;
  // Solo alarmamos cuando hay un problema real.
  if (summary.estado_health !== 'stuck' && summary.estado_health !== 'atrasado') {
    return null;
  }

  const isStuck = summary.estado_health === 'stuck';
  const tone = isStuck
    ? 'border-orange bg-orange-10/40 text-orange'
    : 'border-blue bg-blue-10/40 text-blue';
  const Icon = isStuck ? AlertTriangle : Activity;
  const titulo = isStuck ? 'Cola de emails colgada' : 'Cola de emails atrasada';

  let detalle: string;
  if (isStuck) {
    detalle =
      `Hay ${summary.overdue} mail${summary.overdue === 1 ? '' : 's'} ` +
      `vencido${summary.overdue === 1 ? '' : 's'} y no se enviaron mails en la última hora. ` +
      `Probable que el cron de envío esté caído — avisale al equipo técnico.`;
  } else {
    detalle =
      `${summary.overdue} mail${summary.overdue === 1 ? '' : 's'} programado${summary.overdue === 1 ? '' : 's'} ` +
      `excede${summary.overdue === 1 ? '' : 'n'} su horario en >10 min. La cola sigue mandando ` +
      `(${summary.recent_sent_1h} en la última hora) pero está saturada.`;
  }

  return (
    <div className={`card-premium mb-4 flex items-start gap-3 border-l-4 p-4 ${tone}`}>
      <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-white/60">
        <Icon size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="kicker">{titulo}</p>
        <h4 className="mt-0.5 text-sm font-bold text-neutral-ink">
          {summary.empresa_nombre}
        </h4>
        <p className="mt-1 text-xs text-neutral-muted">{detalle}</p>
      </div>
    </div>
  );
}
